import ChainLogo from '@components/ChainLogo'
import EmptyList from '@components/EmptyList'
import NFTDisplayer from '@components/NftDisplayer'
import useBalances from '@plug-core/hooks/useBalances'
import { Chain, NFT } from '@plug-core/interfaces/common'
import { Avatar, Chip, Surface, cn } from '@ui'
import { ROUTES } from '@utils/routes'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import React, { FC, useState } from 'react'
import { HiChevronDown } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'

interface Props {
  onNftClick?: (nft: NFT) => void
  fromSend?: boolean
  nftChain?: Chain
}

export const NftList: FC<Props> = ({ onNftClick, fromSend, nftChain }) => {
  const { walletNfts, nftsLoading } = useBalances()
  const [expanded, setExpanded] = useState('')
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()

  const renderNftItems = (item: NFT) => {
    return (
      <button
        type="button"
        onClick={() =>
          onNftClick
            ? onNftClick(item)
            : navigate(
                ROUTES.NFT_DETAILS(
                  item.collection,
                  item.standard == 'ordinal' ? item.number.toString() : item.index.toString()
                ),
                {
                  state: { item },
                }
              )
        }
        className="block cursor-pointer overflow-hidden rounded-sm border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        key={item.id}
      >
        {item && <NFTDisplayer nft={item} className="rounded-sm" />}
      </button>
    )
  }

  const renderAccordionItems = () => {
    let collections = walletNfts
    if (nftChain) {
      collections = collections.filter((collection) => collection.chain === nftChain)
    }

    return collections.map((entry) => {
      const isExpanded = expanded === entry.canisterId
      const panelId = `nft-collection-${entry.canisterId}`

      return (
        <Surface key={entry.canisterId} padding="none" className="overflow-hidden">
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={panelId}
            onClick={() =>
              setExpanded((prev) => (prev === entry.canisterId ? '' : entry.canisterId))
            }
            className="flex min-h-14 w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left font-text text-fg transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
          >
            <Avatar src={entry.icon} size={24} fallback={entry.name} />
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{entry.name}</span>
            {entry.chain == Chain.BTC ? (
              <ChainLogo chain={Chain.BTC} size={24} />
            ) : (
              <Chip tone="neutral" className="tabular">
                {entry.nfts.length}
              </Chip>
            )}
            <HiChevronDown
              aria-hidden
              className={cn(
                'size-5 shrink-0 text-fg-3 transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </button>

          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                id={panelId}
                initial={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-2 gap-4 px-4 pt-2 pb-4">
                  {entry.nfts.map((nft) => renderNftItems(nft))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Surface>
      )
    })
  }

  if (!nftsLoading && !fromSend && walletNfts.length === 0)
    return <EmptyList title="No collectibles available" description="Your NFTs will appear here" />

  return <div className="flex flex-1 flex-col gap-2">{renderAccordionItems()}</div>
}
